import type { ServicesListQuery } from "@/types/api";

// Domain identifier types. Naming the inputs to the key builders (instead of
// bare `string`) documents what each key segment means and prevents accidental
// argument swaps. They remain assignable from string, so callers pass plain
// values with no ceremony.
export type ServiceName = string;
export type JobName = string;
export type SchemaId = string;
export type SuiteId = string;
export type ScriptName = string;

// Centralized TanStack Query keys. Prefix-based so a single invalidate (e.g.
// `servicesRoot`) refreshes a resource's list, detail and sub-resource queries
// together.
export const queryKeys = {
  jobs: ["jobs"] as const,
  jobCheckpoints: (name: JobName) => ["jobs", name, "checkpoints"] as const,
  jobMetrics: (name: JobName) => ["jobs", name, "metrics"] as const,
  jobWorkers: (name: JobName) => ["jobs", name, "workers"] as const,
  workspaces: ["workspaces"] as const,
  servicesRoot: ["services"] as const,
  services: (query: ServicesListQuery) => ["services", query] as const,
  service: (name: ServiceName) => ["services", name] as const,
  serviceResources: (name: ServiceName) =>
    ["services", name, "resources"] as const,
  tokens: (service: ServiceName) => ["tokens", service] as const,
  health: ["health"] as const,
  systemStats: ["systemStats"] as const,
  entitlements: ["entitlements"] as const,
  scripts: (service: ServiceName) => ["scripts", service] as const,
  script: (service: ServiceName, filename: ScriptName) =>
    ["scripts", service, filename] as const,
  testSuites: ["testSuites"] as const,
  testSuite: (id: SuiteId) => ["testSuites", id] as const,
  schemasRoot: ["schemas"] as const,
  schemas: (service: ServiceName) => ["schemas", service] as const,
  schema: (service: ServiceName, id: SchemaId) =>
    ["schemas", service, id] as const,
};
